import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { TERMS_SECTIONS } from "@/pages/Terms";

function Field({ label, testId, ...inputProps }) {
  return (
    <div>
      <label className="block text-[10px] tracking-overline uppercase text-[#8A8782] mb-2">
        {label}
      </label>
      <input
        required
        data-testid={testId}
        className="w-full border-b border-[#D1CEC2] bg-transparent text-lg py-3 focus:border-[#2F2E2E] outline-none"
        {...inputProps}
      />
    </div>
  );
}

function TermsStep({ onBack, onAgree, loading }) {
  const [reachedEnd, setReachedEnd] = useState(false);

  function onScroll(e) {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      setReachedEnd((prev) => {
        if (!prev) trackEvent("click", "accept-terms-completed");
        return true;
      });
    }
  }

  return (
    <div data-testid="accept-terms-step">
      <button
        type="button"
        onClick={onBack}
        data-testid="terms-back-btn"
        className="inline-flex items-center gap-2 text-[#5C5A56] hover:text-[#1F1E1D] text-[10px] tracking-overline uppercase font-medium mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
        Back to your details
      </button>
      <h3 className="font-display text-3xl tracking-tight leading-none mb-2 text-[#1F1E1D]">
        Our Terms &amp; Conditions
      </h3>
      <p className="text-sm text-[#5C5A56] mb-4">
        One last step. Please read through to the end, then agree to secure
        your date.
      </p>
      <div
        onScroll={onScroll}
        data-testid="terms-scroll-box"
        className="h-72 md:h-80 overflow-y-auto border border-[#D1CEC2] bg-white p-5 space-y-5"
      >
        {TERMS_SECTIONS.map(([title, body]) => (
          <div key={title}>
            <div className="text-[#1F1E1D] font-medium text-sm mb-1">{title}</div>
            {body.split("\n\n").map((p, i) => (
              <p key={i} className="text-[#5C5A56] text-[13px] leading-relaxed mb-2">
                {p}
              </p>
            ))}
          </div>
        ))}
        <p className="text-[#8A8782] text-[13px] pt-2 border-t border-[#E5E3DB]">
          These Terms &amp; Conditions form the basis of the agreement between
          the Customer and Wedding Video Essex LTD.
        </p>
      </div>
      {!reachedEnd && (
        <p className="text-[11px] text-[#8A8782] mt-3" data-testid="terms-scroll-hint">
          Scroll to the end of the terms to continue.
        </p>
      )}
      <button
        type="button"
        onClick={onAgree}
        disabled={!reachedEnd || loading}
        data-testid="accept-submit-btn" data-track-click="accept-submitted"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-50"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
        I Agree &amp; Secure My Date
      </button>
    </div>
  );
}

export function AcceptModal({ open, onClose, quote, selectedPkgId }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [addr, setAddr] = useState("");
  const [mobile, setMobile] = useState("");
  const [weddingDate, setWeddingDate] = useState(quote.wedding_date || "");
  const [venue, setVenue] = useState(quote.venue_name || "");
  const [addons, setAddons] = useState([]);
  const [step, setStep] = useState("form");
  const [loading, setLoading] = useState(false);
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const prefilledRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      startedRef.current = false;
      submittedRef.current = false;
      setStep("form");
      trackEvent("click", "accept-modal-opened");
      if (!prefilledRef.current) {
        prefilledRef.current = true;
        axios
          .get(`${API}/public/quotes/${quote.slug}/accept-prefill`)
          .then((r) => {
            const d = r.data || {};
            setP1((v) => v || d.partner1_full_name || "");
            setP2((v) => v || d.partner2_full_name || "");
            setMobile((v) => v || d.mobile_number || "");
            setAddr((v) => v || d.home_address || "");
            setWeddingDate((v) => v || d.wedding_date || "");
            setVenue((v) => v || d.wedding_venue || "");
          })
          .catch(() => {});
      }
    }
  }, [open, quote.slug]);

  if (!open) return null;

  function markStarted() {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("click", "accept-form-started");
    }
  }

  function handleClose() {
    if (startedRef.current && !submittedRef.current) {
      trackEvent("click", "accept-form-abandoned");
    }
    onClose();
  }

  function toTerms(e) {
    e.preventDefault();
    trackEvent("click", "accept-terms-viewed");
    setStep("terms");
  }

  async function submit() {
    setLoading(true);
    submittedRef.current = true;
    try {
      const r = await axios.post(`${API}/public/quotes/${quote.slug}/checkout`, {
        selected_package_id: selectedPkgId,
        partner1_full_name: p1,
        partner2_full_name: p2,
        home_address: addr,
        mobile_number: mobile,
        wedding_date: weddingDate || null,
        wedding_venue: venue || null,
        addons,
        terms_accepted: true,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        toast.success("Taking you to secure payment…");
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Quote accepted. Welcome to the family!");
      nav(`/q/${quote.slug}/accepted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
      data-testid="accept-modal"
    >
      <div className="bg-[#FAF9F6] max-w-xl w-full p-8 md:p-10 relative border border-[#D1CEC2] max-h-[92vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 text-[#5C5A56] hover:text-[#1F1E1D]"
          aria-label="Close"
          data-testid="accept-modal-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        {step === "terms" ? (
          <TermsStep onBack={() => setStep("form")} onAgree={submit} loading={loading} />
        ) : (
          <>
            <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-3 font-medium">
              Secure Your Date
            </div>
            <h3 className="font-display text-4xl tracking-tight leading-none mb-4 text-[#1F1E1D]">
              Let's make it official.
            </h3>
            <p className="text-sm text-[#5C5A56] mb-8">
              We've filled in what we know. Please check everything is correct and
              edit anything that needs fixing before you confirm.
            </p>
            <form onSubmit={toTerms} className="space-y-6" data-testid="accept-form">
              <Field
                label="Partner 1 Full Name"
                testId="accept-partner1-full"
                value={p1}
                onChange={(e) => { markStarted(); setP1(e.target.value); }}
              />
              <Field
                label="Partner 2 Full Name"
                testId="accept-partner2-full"
                value={p2}
                onChange={(e) => { markStarted(); setP2(e.target.value); }}
              />
              <div>
                <label className="block text-[10px] tracking-overline uppercase text-[#8A8782] mb-2">
                  Home Address
                </label>
                <textarea
                  required
                  rows={3}
                  value={addr}
                  onChange={(e) => { markStarted(); setAddr(e.target.value); }}
                  data-testid="accept-home-address"
                  className="w-full border border-[#D1CEC2] bg-transparent text-base p-3 focus:border-[#2F2E2E] outline-none resize-none"
                />
              </div>
              <Field
                label="Mobile Number"
                testId="accept-mobile-number"
                type="tel"
                value={mobile}
                onChange={(e) => { markStarted(); setMobile(e.target.value); }}
                placeholder="07xxx xxx xxx"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Wedding Date"
                  testId="accept-wedding-date"
                  type="date"
                  value={weddingDate}
                  onChange={(e) => { markStarted(); setWeddingDate(e.target.value); }}
                />
                <Field
                  label="Wedding Venue"
                  testId="accept-wedding-venue"
                  value={venue}
                  onChange={(e) => { markStarted(); setVenue(e.target.value); }}
                  placeholder="e.g. Gaynes Park"
                />
              </div>
              {(quote.second_photographer_offer || quote.documentary_edit_offer || quote.content_creator_offer) && (
                <div className="border border-[#CCBA56]/50 bg-[#CCBA56]/5 p-5" data-testid="accept-addons">
                  <div className="text-[10px] tracking-overline uppercase text-[#8A6D1A] font-medium mb-3">
                    Optional Extras
                  </div>
                  <div className="space-y-3">
                    {quote.second_photographer_offer && (
                      <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={addons.includes("second_photographer")}
                          onChange={(e) => { markStarted(); setAddons((a) => e.target.checked ? [...a, "second_photographer"] : a.filter((x) => x !== "second_photographer")); }}
                          data-testid="addon-second-photographer"
                          className="mt-0.5 w-4 h-4 accent-[#CCBA56]"
                        />
                        <span className="text-sm text-[#1F1E1D]">
                          Second photographer, full day coverage{" "}
                          <span className="font-semibold text-[#8A6D1A]">
                            +£{Number(quote.second_photographer_offer_price ?? 450).toLocaleString("en-GB")}
                          </span>
                        </span>
                      </label>
                    )}
                    {quote.documentary_edit_offer && (
                      <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={addons.includes("documentary_edit")}
                          onChange={(e) => { markStarted(); setAddons((a) => e.target.checked ? [...a, "documentary_edit"] : a.filter((x) => x !== "documentary_edit")); }}
                          data-testid="addon-documentary-edit"
                          className="mt-0.5 w-4 h-4 accent-[#CCBA56]"
                        />
                        <span className="text-sm text-[#1F1E1D]">
                          The Documentary Edit{" "}
                          <span className="font-semibold text-[#8A6D1A]">
                            +£{Number(quote.documentary_edit_offer_price ?? 199).toLocaleString("en-GB")}
                          </span>{" "}
                          <a
                            href="https://weddingvideoessex.co.uk/investment/documentary-edit"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="documentary-edit-example-link"
                            data-track-click="documentary-edit-example"
                            className="text-[#8A6D1A] underline underline-offset-2 decoration-[#CCBA56] hover:opacity-70"
                          >
                            See an example
                          </a>
                        </span>
                      </label>
                    )}
                    {quote.content_creator_offer && (
                      <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={addons.includes("content_creator")}
                          onChange={(e) => { markStarted(); setAddons((a) => e.target.checked ? [...a, "content_creator"] : a.filter((x) => x !== "content_creator")); }}
                          data-testid="addon-content-creator"
                          className="mt-0.5 w-4 h-4 accent-[#CCBA56]"
                        />
                        <span className="text-sm text-[#1F1E1D]">
                          Dedicated Wedding Content Creator{" "}
                          <span className="font-semibold text-[#8A6D1A]">
                            +£{Number(quote.content_creator_offer_price ?? 695).toLocaleString("en-GB")}
                          </span>
                          <span className="block text-xs text-[#8A8782] mt-0.5">
                            Vertical social coverage, sneak peek reel within 24 hours, 3 edited reels, and every raw clip transferred to you.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              )}
              <button
                type="submit"
                data-testid="accept-continue-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
              >
                Continue to Terms &amp; Conditions
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
